import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { GA4EventName, GA4FeatureCategory } from '@jsmdg/tracking';
import { Breakpoint, type SliderConfig, SlidingDirections } from '@jsmdg/yoshi';
import { type PageType } from '../../../shared/enums/pageType';
import { type FragmentContext } from '../../../shared/types/fragmentContext';
import { TrackingCategory } from '../../enums/trackingCategory';
import { trackSlide } from '../../helper/trackSlide';
import { LazySlider } from '../LazySlider';
import { Category } from './Category';
import styles from './Categories.module.scss';

const categoriesSliderConfig: SliderConfig = {
    spaceBetween: 12,
    slidesPerView: 0,
    slidesPerGroup: 0,
    showPagination: true,
    showNavigation: true,
    breakpoints: {
        [Breakpoint.SM]: { slidesPerView: 4, slidesPerGroup: 4, spaceBetween: 12 },
        [Breakpoint.MD]: { slidesPerView: 4, slidesPerGroup: 4, spaceBetween: 24 },
        [Breakpoint.LG]: { slidesPerView: 6, slidesPerGroup: 6, spaceBetween: 24 },
    },
};

type CategoriesProps = {
    readonly pageType: PageType;
    readonly verticalPosition: number;
    readonly hasNavigationCreative: boolean;
    readonly withBlockMargin?: boolean;
};

const messages = defineMessages({
    a11yPreviousSlide: { defaultMessage: 'Zur vorherigen Seite' },
    a11yNextSlide: { defaultMessage: 'Zur nächsten Seite' },
});

const Categories = ({
    hasNavigationCreative,
    pageType,
    verticalPosition,
    withBlockMargin,
}: CategoriesProps): JSX.Element | null => {
    const intl = useIntl();
    const {
        tenantConfig: { categoryMap },
    } = useFragmentContext<FragmentContext>();

    const sliderConfig = categoriesSliderConfig;

    const handleOnSlide = (direction: SlidingDirections): void =>
        trackSlide({
            direction,
            category: TrackingCategory.InternalPromotion,
            eventData: {
                eventName: GA4EventName.SlidePromotionItem,
                feature_category: GA4FeatureCategory.PromotionStatic,
                promotion_type: 'Image_Slider',
                position_vertical: verticalPosition?.toString(),
                click_type: `Slide ${direction.toLowerCase()}`,
            },
        });

    if (!categoryMap?.length) {
        return null;
    }

    const slides = categoryMap.map(({ categoryId, label, url }, index) => {
        return {
            key: categoryId,
            content: (
                <Category
                    pageType={pageType}
                    categoryId={categoryId}
                    url={url}
                    label={label}
                    verticalPosition={verticalPosition}
                    hasNavigationCreative={hasNavigationCreative}
                    index={index}
                />
            ),
        };
    });

    return (
        <div
            className={classNames('position-relative w-100', styles.categories, {
                [styles.withBlockMargin]: withBlockMargin,
            })}
        >
            <LazySlider
                a11yNextSlide={intl.formatMessage(messages.a11yNextSlide)}
                a11yPreviousSlide={intl.formatMessage(messages.a11yPreviousSlide)}
                slides={slides}
                config={sliderConfig}
                handleShowNext={() => handleOnSlide(SlidingDirections.Next)}
                handleShowPrevious={() => handleOnSlide(SlidingDirections.Previous)}
                classes={{
                    swiperWrapper: 'p-0-5x',
                    controlsContainer: 'd-none d-sm-block',
                }}
            />
        </div>
    );
};

export { Categories };
